/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。
 */
import { useTranslation } from 'react-i18next'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import type { DemandProcurementMasterRow } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/types'
import { ExpiredBadge } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredBadge'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  rows: DemandProcurementMasterRow[]
  isLoading: boolean
  onEdit: (row: DemandProcurementMasterRow) => void
  onDelete: (row: DemandProcurementMasterRow) => void
  today: CivilDate
  /** 空 state のメッセージ上書き (AC-09)。 */
  emptyLabel?: string
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

export const DemandProcurementPlanMasterTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  today,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columns: Array<MasterDataTableColumn<DemandProcurementMasterRow>> = [
    // [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // design (`design/admin/occto.html` 需要調達計画タブ) は 6 列目 (備考 と 適用開始日 の間。
    // 静的 thead には無く _occtoSyncStatusColumn が挿入する) だが、
    // 行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
    {
      key: 'status',
      header: t('page:adminOcctoMaster.common.column.status'),
      cell: (row) => (
        <ExpiredBadge
          today={today}
          validFrom={row.validFrom}
          validTo={row.validTo}
        />
      ),
    },
    {
      key: 'bgCode',
      header: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.column.bgCode',
      ),
      cell: (row) => row.bgCode,
    },
    {
      // AC-07 (occto-master-audit-fix-bundle.md): 紐づく BG のエリアを表示
      key: 'areaCode',
      header: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.column.areaCode',
      ),
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      key: 'codeCount',
      header: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.column.codeCount',
      ),
      format: 'number',
      cell: (row) => row.codeCount.toLocaleString(),
    },
    {
      key: 'bgCount',
      header: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.column.bgCount',
      ),
      format: 'number',
      cell: (row) => row.bgCount.toLocaleString(),
    },
    {
      key: 'note',
      header: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.column.note',
      ),
      cell: (row) => row.note ?? '',
    },
    {
      key: 'validFrom',
      header: t('page:adminOcctoMaster.common.column.validFrom'),
      cell: (row) => formatCivilDate(row.validFrom),
    },
    {
      key: 'validTo',
      header: t('page:adminOcctoMaster.common.column.validTo'),
      cell: (row) =>
        row.validTo !== undefined ? formatCivilDate(row.validTo) : '—',
    },
    {
      key: 'updatedAt',
      header: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.column.updatedAt',
      ),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
